import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { ArrowLeft, MapPin, User, Phone, Mail, Clock, AlertTriangle, CheckCircle, Radio, Navigation } from 'lucide-react';
import { GoogleMap, Marker, DirectionsRenderer, useLoadScript } from '@react-google-maps/api';
import { cn } from '../utils/cn';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '400px', borderRadius: '12px' };

export default function RideVerificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['fleet-ride-verification', id],
    queryFn: () => fleetService.getRideVerification(id).then((res) => res.data?.data),
  });

  const [directions, setDirections] = useState(null);

  const hasStart = !isLoading && data?.startLatitude != null && data?.startLongitude != null;
  const hasEnd = !isLoading && data?.endLatitude != null && data?.endLongitude != null;

  const onMapLoad = useCallback((map) => {
    if (!hasStart || !hasEnd) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: { lat: parseFloat(data.startLatitude), lng: parseFloat(data.startLongitude) },
        destination: { lat: parseFloat(data.endLatitude), lng: parseFloat(data.endLongitude) },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        }
      }
    );
  }, [hasStart, hasEnd, data?.startLatitude, data?.startLongitude, data?.endLatitude, data?.endLongitude]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-text-secondary dark:text-text-secondary-dark">Loading verification details...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-8 text-text-secondary dark:text-text-secondary-dark">Verification record not found</div>;
  }

  const mapCenter = hasStart
    ? { lat: parseFloat(data.startLatitude), lng: parseFloat(data.startLongitude) }
    : hasEnd
      ? { lat: parseFloat(data.endLatitude), lng: parseFloat(data.endLongitude) }
      : { lat: 59.91, lng: 10.75 };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/ride-verifications')}
        className="flex items-center gap-2 text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Back to Ride Verifications</span>
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary-dark/10 dark:to-primary-dark/5 rounded-2xl p-6 lg:p-8 border border-primary/20 dark:border-primary-dark/20 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-1">
              Ride Verification Detail
            </h1>
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark font-mono">
              Ride: {data.rideId}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={cn(
              'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium',
              data.status === 'LIVE'
                ? 'bg-info/10 text-info'
                : 'bg-success-light dark:bg-success-dark text-success'
            )}>
              {data.status === 'LIVE' ? <Radio className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              {data.status}
            </span>
            {data.isSuspicious && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-danger-light dark:bg-danger-dark text-danger">
                <AlertTriangle className="w-4 h-4" />
                Suspicious
              </span>
            )}
          </div>
        </div>

        {data.isSuspicious && data.suspiciousReason && (
          <div className="mt-4 p-3 bg-danger/10 border border-danger/20 rounded-lg">
            <p className="text-sm text-danger font-medium">Reason: {data.suspiciousReason}</p>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark p-6 shadow-lg">
        <h2 className="text-xl font-bold text-text dark:text-text-dark mb-4 flex items-center gap-2">
          <Navigation className="w-5 h-5 text-primary" />
          Verification Map
        </h2>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={hasStart && hasEnd ? 13 : 15}
            onLoad={onMapLoad}
          >
            {directions ? (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#4F46E5',
                    strokeOpacity: 0.8,
                    strokeWeight: 5,
                  },
                }}
              />
            ) : null}
            {hasStart && (
              <Marker
                position={{ lat: parseFloat(data.startLatitude), lng: parseFloat(data.startLongitude) }}
                label={{ text: 'S', color: 'white', fontWeight: 'bold' }}
                title="Start Location"
              />
            )}
            {hasEnd && (
              <Marker
                position={{ lat: parseFloat(data.endLatitude), lng: parseFloat(data.endLongitude) }}
                label={{ text: 'E', color: 'white', fontWeight: 'bold' }}
                title="End Location"
              />
            )}
          </GoogleMap>
        ) : (
          <div className="h-[400px] bg-surface-secondary dark:bg-surface-secondary-dark rounded-xl flex items-center justify-center text-text-secondary dark:text-text-secondary-dark">
            Loading map...
          </div>
        )}
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Driver Info */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark p-6 shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Driver Information
          </h2>
          <div className="space-y-4">
            <InfoRow icon={User} label="Name" value={data.driver?.name} />
            <InfoRow icon={Phone} label="Phone" value={data.driver?.phone} />
            <InfoRow icon={Mail} label="Email" value={data.driver?.email} />
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark p-6 shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-info" />
            Rider Information
          </h2>
          <div className="space-y-4">
            <InfoRow icon={User} label="Name" value={data.customer?.name} />
            <InfoRow icon={Phone} label="Phone" value={data.customer?.phone} />
            <InfoRow icon={Mail} label="Email" value={data.customer?.email} />
          </div>
        </div>

        {/* Trip Details */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark p-6 shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Trip Details
          </h2>
          <div className="space-y-4">
            <InfoRow icon={MapPin} label="Pickup" value={data.ride?.pickupAddress} />
            <InfoRow icon={MapPin} label="Destination" value={data.ride?.destinationAddress} />
            <InfoRow label="Status" value={data.ride?.status} />
            <InfoRow label="Fare" value={data.ride?.actualFare ? `NOK ${data.ride.actualFare}` : data.ride?.estimatedFare ? `NOK ${data.ride.estimatedFare} (est.)` : '—'} />
            <InfoRow label="Distance" value={data.ride?.distanceKm ? `${data.ride.distanceKm} km` : '—'} />
          </div>
        </div>

        {/* Coordinates & Timestamps */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark p-6 shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Verification Data
          </h2>
          <div className="space-y-4">
            <InfoRow label="Start Coords" value={hasStart ? `${data.startLatitude}, ${data.startLongitude}` : '—'} mono />
            <InfoRow label="Start Time" value={formatDate(data.startTimestamp)} />
            <InfoRow label="End Coords" value={hasEnd ? `${data.endLatitude}, ${data.endLongitude}` : '—'} mono />
            <InfoRow label="End Time" value={formatDate(data.endTimestamp)} />
            <InfoRow label="Created" value={formatDate(data.createdAt)} />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
      {Icon && <Icon className="w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">{label}</p>
        <p className={cn(
          'text-sm font-medium text-text dark:text-text-dark break-all',
          mono && 'font-mono'
        )}>
          {value || '—'}
        </p>
      </div>
    </div>
  );
}
